import { Unsubscribe } from 'firebase/database';
import { AuditActionTypes } from '../ts/enums';
import StatefulSettings from './StatefulSettings';
import { NotificationType } from './messaging';
import { IKeyedItem } from '../ts/Definitions';
import { decrypt, subscribe, unsubscribe } from '../utilities/utils';
import { v1 } from 'uuid';


export interface IEntityCache {
    dispose: () => void;
    get: () => IKeyedItem[];
}

export class CacheRecord<T extends IKeyedItem> implements IEntityCache {

    itemsCache: T[] | null = null;
    private get items() {
        if (!this.itemsCache) {
            this.itemsCache = [];
            this.unsubscribe = this.getCb(this.statefulSettings.getControl().userid, this.onUpdate);
        }
        return this.itemsCache;
    }
    private unsubscribe: Unsubscribe | null = null;
    private statefulSettings: StatefulSettings;
    private description: string;

    private itemsChangedListeners: (() => void)[] = [];

    private addCb: (userid: string, item: T) => void;
    private removeCb: (userid: string, key: string) => void;
    private getCb: (userid: string, itemsCb: (items: any) => void) => Unsubscribe;
    private onUpdateCb: ((items: T[]) => void) | undefined;
    private convertFunc: ((i: any) => any) | undefined;
    private getName: (i: any) => string;

    constructor(description: string,
        settings: StatefulSettings,
        add: (user: string, item: T) => void,
        remove: (user: string, key: string) => void,
        get: (user: string, onUpdateCb: (items: any) => void) => Unsubscribe,
        getName: (i: any) => string,
        onUpdate?: (items: T[]) => void,
        convertFunc?: (i: any) => any) {
        this.description = description;
        this.statefulSettings = settings;
        this.addCb = add;
        this.removeCb = remove;
        this.getCb = get;
        this.onUpdateCb = onUpdate;
        this.convertFunc = convertFunc;
        this.getName = getName;
    }

    public unsubscribeItems = (listener: () => void): void => {
        unsubscribe(this.itemsChangedListeners, listener);
    }

    public subscribeItems = (listener: () => void): Unsubscribe => {
        subscribe(this.itemsChangedListeners, listener);
        return () => this.unsubscribeItems(listener);
    }

    public add = (item: T, suppressAudit?: boolean, suppressAlert?: boolean): void => {
        if (!item.key) item.key = v1();
        const idx = this.items.findIndex(t => t?.key === item.key);
        if (idx > -1) this.items[idx] = item;
        else this.items.push(item);
        void this.addCb(this.statefulSettings.getControl().userid, item);
        if (!suppressAudit) {
            this.statefulSettings.auditUserAction(`${this.description} ${this.getName(item)} ${idx > -1 ? 'updated' : 'created'}`, AuditActionTypes.Session);
        }
        if (!suppressAlert) {
            this.statefulSettings.showMessage({ message: `${this.description} ${this.getName(item)} ${idx > -1 ? 'updated' : 'created'}`, notificationType: NotificationType.SettingsInfo });
        }
        this.statefulSettings.fireListeners();
        this.fireListeners();
    }

    public remove = (item: T): void => {
        if (!this.items || !item || !item.key) return;
        const idx = this.items.findIndex(t => t?.key === item.key);
        if (idx === -1) return;
        delete this.items[idx];
        void this.removeCb(this.statefulSettings.getControl().userid, item.key);
        this.statefulSettings.showMessage({ message: `${this.description} ${this.getName(item)} deleted`, notificationType: NotificationType.SettingsInfo });
        this.statefulSettings.auditUserAction(`${this.description} ${this.getName(item)} deleted`, AuditActionTypes.Session);
        this.statefulSettings.fireListeners();
        this.fireListeners();
    }

    public get = (): T[] => {
        return this.items;
    }

    public fireListeners = () => {
        if (this.itemsChangedListeners.length > 0) {
            this.itemsChangedListeners.forEach(listener => listener());
        }
    }

    public onUpdate = (snapshot: any): void => {
        this.itemsCache = [];
        const newItems = snapshot as Record<string, T>;
        if (!newItems) return;
        Object.keys(newItems).forEach((key) => {
            newItems[key] = decrypt(newItems[key] as any as string, {}) as T;
            if (this.convertFunc) newItems[key] = this.convertFunc(newItems[key]);
        });
        this.itemsCache = Object.values(newItems) as T[];
        this.fireListeners();
        this.statefulSettings.fireListeners(); // means view get updated with the changes too
        if (this.onUpdateCb) this.onUpdateCb(Object.values(newItems));
    }

    public dispose = () => {
        if (this.unsubscribe) this.unsubscribe();
    }
}